import * as React from 'react'
import { ReactElement, useContext } from 'react'
import { Tree, TreeItem, TreeItemLayout } from '@fluentui/react-components/unstable'
import {
    Body1,
    Button,
    Card,
    makeStyles,
    mergeClasses,
    shorthands,
    Text,
    Title1,
    tokens,
} from '@fluentui/react-components'
import IdentityContext, { Identity, IdentityContextProps } from '../../context/IdentityContext'
import { bundleIcon, PersonSwapFilled, PersonSwapRegular, SignOutFilled, SignOutRegular } from '@fluentui/react-icons'
import { AppAction } from '../../config/appConfig'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles({
    container: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    user: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    username: {
        flexGrow: 1,
        flexShrink: 1,
        overflowWrap: 'break-word',
    },
    vertical: {
        flexDirection: 'column',
    },
    buttons: {
        whiteSpace: 'nowrap',
    },
    hostName: {
        ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    },
})

const PersonSwap = bundleIcon(PersonSwapFilled, PersonSwapRegular)
const SignOut = bundleIcon(SignOutFilled, SignOutRegular)

export default function SelectHost(): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { username, hosts } = useContext<IdentityContextProps>(IdentityContext)
    const { isViewport } = useViewport()

    return (
        <div className={styles.container}>
            <Title1>{translate('authentication.host.select.title')}</Title1>
            <div className={mergeClasses(styles.user, isViewport('<=', Viewport.Medium) && styles.vertical)}>
                <Body1 className={styles.username}>
                    {translate('authentication.host.select.subtitle')}{' '}
                    <Text italic>{username || translate('base.unknown')}.</Text>
                </Body1>
                <div className={mergeClasses(styles.buttons)}>
                    <Button
                        appearance="subtle"
                        icon={<PersonSwap />}
                        onClick={() => Identity.current?.logout(AppAction.LogOut, username, false)}
                    >
                        {translate('authentication.host.select.account.change')}
                    </Button>
                    <Button
                        appearance="subtle"
                        icon={<SignOut />}
                        onClick={() => Identity.current?.logout(AppAction.LogOut, username, true)}
                    >
                        {translate('authentication.host.select.account.logoff')}
                    </Button>
                </div>
            </div>
            <Card>
                <Tree aria-label="Omgevingen">
                    {(hosts || [])
                        .sort((a, b) => a.Name.localeCompare(b.Name))
                        .map(host => (
                            <TreeItem
                                key={host.RowKey}
                                itemType="leaf"
                                onClick={() => Identity.current?.selectHost(host)}
                            >
                                <TreeItemLayout className={styles.hostName}>{host.Name}</TreeItemLayout>
                            </TreeItem>
                        ))}
                </Tree>
            </Card>
        </div>
    )
}
